import {
  APPOINTMENT_STATUS,
  CANCELED_VARIANTS,
  config,
  localeMap,
} from "../consts/constant";
import i18n from "../i18";

export const isArrayContainsValidValue = (arr) => {
  if (!arr || !arr?.length) {
    return false;
  }
  if (arr.length) {
    if (!arr[0] || (typeof arr[0] === "string" && arr[0] === "null")) {
      return false;
    }
  }
  return true;
};

export const getValidUrlParam = (url) => {
  if (url) {
    return url.replace(/ /g, "+");
  }
  return "";
};

export const getInitials = (name, endWithFullStop) => {
  if (name) {
    return `${name.slice(0, 1)}${endWithFullStop ? "." : ""}`;
  }
  return "";
};

export const getAppointmentType = (appointmentType) => {
  if (!appointmentType) return "";
  if (appointmentType === "in-office") return "In-office";
  if (appointmentType === "teledental") return "Teledental";
  return appointmentType;
};

export const getTimeZone = (timeZone) => {
  if (!timeZone) return "";
  return timeZone?.trim()?.split(" ")?.[0];
};

export const metreToMiles = (metres) => {
  if (!metres) {
    return "";
  }
  return `${Math.round(metres / 1609)} mi`;
};

export const displayDistance = (
  distanceInMiles,
  preference,
  t,
  includeAway = true,
) => {
  if (distanceInMiles === undefined || distanceInMiles === null) return "";
  if (preference === "km") {
    const km = distanceInMiles * 1.60934;
    const value = km.toFixed(2);
    if (!t) return `${value} km`;
    return `${value} ${includeAway ? t("KM_AWAY") : "km"}`;
  }
  const value = Number(distanceInMiles).toFixed(2);
  if (!t) return `${value} miles`;
  return `${value} ${includeAway ? t("MILES_AWAY") : "miles"}`;
};

export const removeAllDelimeterOccurencesFromString = (str, delimeter = "") => {
  return str.split(`${delimeter}`).join("");
};

export const getUserCountry = (addressComponent) => {
  if (!addressComponent?.length) {
    return "";
  }
  for (let i = addressComponent.length - 1; i >= 0; i--) {
    if (addressComponent[i]?.types.includes("country")) {
      return addressComponent[i].long_name;
    }
  }
  return "";
};

export const getCountryFromLatLng = async ({ lat, lng }) => {
  let country = "";
  try {
    const latlng = {
      lat,
      lng,
    };
    if (window.google?.maps?.Geocoder) {
      const res = await new window.google.maps.Geocoder().geocode({
        location: latlng,
        language: "en",
      });
      country = getUserCountry(res.results[0]?.address_components);
    }
  } catch (err) {
    console.log(err);
  }
  return country;
};

export const areTwoNonNestedObjectsEqual = (obj1, obj2) => {
  const obj1Keys = Object.keys(obj1).sort();
  const obj2Keys = Object.keys(obj2).sort();
  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }

  return obj1Keys.every((key) => {
    const obj1Value = obj1[key];
    const obj2Value = obj2[key];
    return obj1Value === obj2Value;
  });
};

export const areTwoArrayOfNonNestedObjectsEqual = (arr1, arr2) => {
  if (!arr1?.length && !arr2?.length) {
    return true;
  }
  if (arr1?.length !== arr2?.length) {
    return false;
  }
  if (arr1?.length === arr2?.length) {
    let ctr = 0;
    arr1.forEach((array1Obj, idx) => {
      if (areTwoNonNestedObjectsEqual(array1Obj, arr2[idx])) {
        ctr++;
      }
    });
    return ctr === arr1?.length;
  }
};

export const returnTextWithValueIfValueExists = (text, value, valueToShow) => {
  return value ? `${text} ${valueToShow ? valueToShow : value}` : "";
};

export const groupDataAlphabetically = ({ data, keyName }) => {
  const groupedData = {};
  data?.forEach((item) => {
    const firstLetter = /[0-9]/.test(item?.[keyName]?.[0]?.toUpperCase())
      ? "0-9"
      : item?.[keyName]?.[0]?.toUpperCase();
    if (!groupedData[firstLetter]) {
      groupedData[firstLetter] = [];
    }
    groupedData[firstLetter].push(item);
  });
  return groupedData;
};

export const convertTo12Hour = (time) => {
  if (!time) return "";

  let [hours, minutes] = time.split(":");
  hours = parseInt(hours);

  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;

  return `${formattedHours}:${minutes} ${period}`;
};

export const videoCallHandler = (appointmentId) => {
  const currentLang = localeMap(i18n.language) || "en";
  let path = `${process.env.REACT_APP_VIDEO_URL || config.url.VIDEO_CALL_URL}room/${encodeURIComponent(
    appointmentId,
  )}?platformType=patient&lang=${currentLang}`;
  window.open(path);
};

export const sanitizeNoAlphabet = (value) =>
  value?.replace(/[a-z]/gi, "") || "";

export const getSelectedCountryCodeOption = (
  options,
  selectedValue,
  keyName,
) => {
  if (!options?.length || selectedValue === undefined || selectedValue === null)
    return null;

  return (
    options.find((opt) => opt.value === selectedValue) ||
    options.find((opt) => keyName && opt[keyName] === selectedValue) ||
    options.find((opt) => String(opt.value) === String(selectedValue)) ||
    options.find(
      (opt) => keyName && String(opt[keyName]) === String(selectedValue),
    ) ||
    null
  );
};

export const parseFiniteNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const toFiniteNumber = (value, fallback = 0) => {
  const parsedValue = parseFiniteNumber(value);
  return parsedValue === null ? fallback : parsedValue;
};

export const getFiniteLatLng = (latitude, longitude) => {
  const lat = parseFiniteNumber(latitude);
  const lng = parseFiniteNumber(longitude);

  if (lat === null || lng === null) {
    return null;
  }

  return { lat, lng };
};
export const getPasswordValidationError = (password) => {
  if (!password || password.length === 0) {
    return "";
  }
  if (password.length <= 8) {
    return i18n.t("WEAK_PASSWORD");
  }
  if (!/[a-z]/.test(password)) {
    return i18n.t("Password must include a lowercase letter");
  }
  if (!/[A-Z]/.test(password)) {
    return i18n.t("Password must include a capital letter");
  }
  if (!/\d/.test(password)) {
    return i18n.t("Password must include a number");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return i18n.t("Password must include a special character");
  }
  return "";
};

export function normalizeStatus(status) {
  const lower = status?.toLowerCase() ?? "";
  if (CANCELED_VARIANTS.has(lower)) return APPOINTMENT_STATUS.CANCELED;
  return lower;
}

export const getPostalCodeFromCoords = async (latitude, longitude) => {
  if (!window.google?.maps) return null;

  try {
    const geocoder = new window.google.maps.Geocoder();
    const { results } = await geocoder.geocode({
      location: { lat: latitude, lng: longitude },
    });

    // The postal code isn't always in the first result, so search them all
    for (const result of results) {
      const postalComponent = result.address_components.find((component) =>
        component.types.includes("postal_code"),
      );
      if (postalComponent) return postalComponent.long_name;
    }
  } catch (error) {
    // Geocoding failed (ZERO_RESULTS, REQUEST_DENIED, etc.)
  }

  return null;
};
